import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import BubbleChartIcon from '@mui/icons-material/BubbleChart'; // Qualities
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlareIcon from '@mui/icons-material/Flare'; // Essence/Theme
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined'; // Meaning
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const PhenomenologicalAnalysisTranscript = ({ summaryData }) => {
  const [expandedEssence, setExpandedEssence] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedEssence(isExpanded ? panel : false);
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Phenomenological analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Phenomenological Analysis
          <AnalysisLensHelperText lensName="phenomenological" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Overall Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {results.summary || 'No summary provided.'}
          </Typography>
        </Box>

        {/* Essences Accordions */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Identified Essences
        </Typography>

        {results.essences?.length > 0 ? (
          results.essences.map((essence, index) => {
            const panelId = `essence-panel-${index}`;
            const isExpanded = expandedEssence === panelId;

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1.5,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: '0 0 12px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    bgcolor: isExpanded ? 'grey.50' : 'background.paper',
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: 'divider',
                    borderTopLeftRadius: 'inherit',
                    borderTopRightRadius: 'inherit',
                    minHeight: 64,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <FlareIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                      {essence.theme}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2.5}>
                    {/* Qualities */}
                    {essence.qualities?.length > 0 && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <BubbleChartIcon fontSize="inherit" color="secondary" /> Qualities
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, pl: 3 }}>
                          {essence.qualities.map((quality, idx) => (
                            <Chip
                              key={idx}
                              label={quality}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Meaning */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <PsychologyOutlinedIcon fontSize="inherit" color="info" /> Meaning
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 3 }}>
                        {essence.meaning || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Quotes */}
                    {essence.quotes?.length > 0 && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <FormatQuoteIcon fontSize="inherit" color="action" /> Supporting Quotes
                        </Typography>
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {essence.quotes.map((quote, idx) => (
                            <ListItem
                              key={idx}
                              disableGutters
                              sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 1 } }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                <FormatQuoteIcon
                                  fontSize="small"
                                  sx={{ transform: 'scaleX(-1)' }}
                                  color="disabled"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No specific essences identified in this analysis.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PhenomenologicalAnalysisTranscript;
